import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { IndianRupee, PlusCircle, Building2, Save, Package, Plus } from "lucide-react";

const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);

const selectCls = "h-8 text-xs border rounded-md bg-background px-2";

/* Translates a legacy structured plan's components (registration_fee/
   booking_amount/second_instalment/third_instalment/success_fee/addons)
   into the new schema_version:2 shape (instalments + fee_items), with
   numerically identical amounts/conditions/triggers — used by "Convert to
   new pricing model" so an admin can review the translated draft before
   publishing, rather than a silent backend migration. */
const convertLegacyToV2 = (c) => {
  const instalments = [];
  if (Number(c.registration_fee) > 0)
    instalments.push({ key: "registration", label: "Registration Fee", amount: c.registration_fee, activation: "immediate", gates_booking_date: true });
  if (Number(c.booking_amount) > 0)
    instalments.push({ key: "booking", label: "Booking Amount", amount: c.booking_amount, activation: "immediate", gates_booking_date: true });
  const si = c.second_instalment || {};
  if ((si.matrix || []).length)
    instalments.push({ key: "second", label: "Second Instalment", matrix: si.matrix, activation: "after_effective_booking_date" });
  const ti = c.third_instalment || {};
  if ((ti.matrix || []).length)
    instalments.push({ key: "third", label: "Third / Final Instalment", matrix: ti.matrix, activation: "milestone", trigger: ti.trigger || "" });

  const fee_items = [];
  if (Number(si.per_pte_learner) > 0)
    fee_items.push({ key: "pte", label: "PTE per learner", amount: si.per_pte_learner, per_unit_of: "pte_learners", reimbursement_amount: "0", schedule: [{ instalment_key: "second", portion: 1 }] });
  if (Number(si.per_dependent) > 0 || Number(si.per_dependent_reimbursement) > 0) {
    const schedule = [];
    if (ti.per_dependent || ti.per_dependent_reimbursement)
      schedule.push({ instalment_key: "second", portion: 0.5 }, { instalment_key: "third", portion: 0.5 });
    else
      schedule.push({ instalment_key: "second", portion: 1 });
    fee_items.push({ key: "dependent_child", label: "Dependent child fee",
      amount: String((Number(si.per_dependent) || 0) + (Number(si.per_dependent_reimbursement) || 0)),
      per_unit_of: "dependents", reimbursement_amount: si.per_dependent_reimbursement || "0", schedule });
  }
  if (c.success_fee && Number(c.success_fee.amount) > 0) {
    instalments.push({ key: "success_fee", label: c.success_fee.label || "Success Fee",
      amount: c.success_fee.amount, activation: "milestone", trigger: c.success_fee.trigger || "" });
  }
  // Note: `c.addons` (an optional catalog customers opt into via inputs.addons,
  // separate from the automatic per_pte_learner/per_dependent fees above) is
  // intentionally NOT converted here — it's a different concept (a menu of
  // optional extras, not an automatically-applied fee) and translating it
  // into an automatic fee_item would silently start auto-billing something
  // that was previously opt-in only.
  return { schema_version: 2, instalments, fee_items };
};

const ACTIVATION_OPTIONS = [
  { value: "immediate", label: "Due as soon as the plan is created" },
  { value: "after_effective_booking_date", label: "After the booking is confirmed" },
  { value: "milestone", label: "When a specific stage happens (e.g. visa granted)" },
  { value: "after", label: "N days after another instalment is paid" },
];

const KNOWN_TRIGGERS = ["positive_skill_assessment", "successful_admission", "ita_received"];

/* Plain-language replacement for raw JSON `{"field": value}` condition
   objects — used for both matrix `if` rows and fee-item `condition`.
   Each row is a field name + a value (Yes/No for booleans, free text
   otherwise); multiple rows AND together, matching `_match_matrix`'s
   existing dict-equality semantics exactly, so the backend needs no
   changes — this only swaps the input method. */
const ConditionBuilder = ({ condition, onChange, testPrefix }) => {
  const entries = Object.entries(condition || {});
  const setEntry = (i, key, value) => {
    const next = [...entries]; next[i] = [key, value];
    onChange(Object.fromEntries(next.filter(([k]) => k)));
  };
  const addEntry = () => onChange({ ...(condition || {}), "": true });
  const removeEntry = (i) => onChange(Object.fromEntries(entries.filter((_, idx) => idx !== i)));
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v], i) => {
        const isBool = typeof v === "boolean";
        return (
          <div key={i} className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-muted-foreground">If</span>
            <Input data-testid={`${testPrefix}-cond-field-${i}`} className="h-7 text-xs w-40" placeholder="e.g. spouse_migrating" value={k} onChange={(e) => setEntry(i, e.target.value, v)} />
            <span className="text-xs text-muted-foreground">is</span>
            <select className={selectCls} value={isBool ? "yesno" : "text"}
              onChange={(e) => setEntry(i, k, e.target.value === "yesno" ? true : "")}>
              <option value="yesno">Yes / No</option>
              <option value="text">a specific value</option>
            </select>
            {isBool ? (
              <select data-testid={`${testPrefix}-cond-value-${i}`} className={selectCls} value={String(v)} onChange={(e) => setEntry(i, k, e.target.value === "true")}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : (
              <Input data-testid={`${testPrefix}-cond-value-${i}`} className="h-7 text-xs w-32" value={v} onChange={(e) => setEntry(i, k, e.target.value)} />
            )}
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => removeEntry(i)}>×</Button>
          </div>
        );
      })}
      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`${testPrefix}-cond-add`} onClick={addEntry}>
        <Plus className="h-3.5 w-3.5 mr-1" />Add condition</Button>
    </div>
  );
};

const InstalmentRow = ({ inst, index, allKeys, onChange, onRemove, onToggleMatrix }) => {
  const set = (patch) => onChange(index, { ...inst, ...patch });
  const setMatrixField = (i, patch) => {
    const m = [...(inst.matrix || [])]; m[i] = { ...m[i], ...patch }; set({ matrix: m });
  };
  const addRow = () => set({ matrix: [...(inst.matrix || []), { if: {}, amount: "0.00" }] });
  const removeRow = (i) => set({ matrix: (inst.matrix || []).filter((_, idx) => idx !== i) });
  const isAfter = (inst.activation || "").startsWith("after:");
  const afterKey = isAfter ? inst.activation.slice(6) : "";
  return (
    <div className="border rounded-md p-2 space-y-2" data-testid={`v2-instalment-${index}`}>
      <div className="flex items-center gap-2">
        <Input data-testid={`v2-inst-key-${index}`} className="h-8 w-32 font-mono text-xs" placeholder="key" value={inst.key || ""} onChange={(e) => set({ key: e.target.value })} />
        <Input data-testid={`v2-inst-label-${index}`} className="h-8 flex-1" placeholder="Label" value={inst.label || ""} onChange={(e) => set({ label: e.target.value })} />
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2" data-testid={`v2-inst-remove-${index}`} onClick={() => onRemove(index)}>×</Button>
      </div>
      <div className="grid grid-cols-3 gap-2 items-end">
        <div className="space-y-1"><Label className="text-[10px]">GST rate (0-1)</Label>
          <Input data-testid={`v2-inst-gst-${index}`} type="number" step="0.01" className="h-8" value={inst.gst_rate ?? ""} onChange={(e) => set({ gst_rate: Number(e.target.value) })} /></div>
        <div className="space-y-1"><Label className="text-[10px]">When is this due?</Label>
          <select data-testid={`v2-inst-activation-${index}`} className={selectCls + " w-full"}
            value={isAfter ? "after" : (inst.activation || "immediate")}
            onChange={(e) => { const v = e.target.value; set({ activation: v === "after" ? `after:${afterKey}` : v }); }}>
            {ACTIVATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select></div>
        {inst.activation === "milestone" && (
          <div className="space-y-1"><Label className="text-[10px]">Stage / event name</Label>
            <Input data-testid={`v2-inst-trigger-${index}`} className="h-8" list="finance-known-triggers" placeholder="e.g. visa_granted" value={inst.trigger || ""} onChange={(e) => set({ trigger: e.target.value })} /></div>
        )}
      </div>
      {isAfter && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1"><Label className="text-[10px]">Which instalment must be paid first?</Label>
            <select data-testid={`v2-inst-after-${index}`} className={selectCls + " w-full"} value={afterKey}
              onChange={(e) => set({ activation: `after:${e.target.value}` })}>
              <option value="">select…</option>
              {allKeys.filter((k) => k !== inst.key).map((k) => <option key={k} value={k}>{k}</option>)}
            </select></div>
          <div className="space-y-1"><Label className="text-[10px]">Days after payment (0 = immediately)</Label>
            <Input data-testid={`v2-inst-delay-${index}`} type="number" min="0" className="h-8" value={inst.delay_days ?? 0} onChange={(e) => set({ delay_days: Number(e.target.value) })} /></div>
        </div>
      )}
      <label className="flex items-start gap-1.5 text-xs">
        <input type="checkbox" className="mt-0.5" data-testid={`v2-inst-gates-${index}`} checked={!!inst.gates_booking_date} onChange={(e) => set({ gates_booking_date: e.target.checked })} />
        <span>Counts toward confirming the booking
          <span className="block text-[10px] text-muted-foreground font-normal">Once every instalment marked this way is paid, the booking is considered confirmed — later instalments set to "After the booking is confirmed" become due, and the Booking Receipt can be issued.</span>
        </span>
      </label>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant={inst.matrix ? "outline" : "default"} onClick={() => onToggleMatrix(index, false)}>Same amount for everyone</Button>
        <Button type="button" size="sm" variant={inst.matrix ? "default" : "outline"} onClick={() => onToggleMatrix(index, true)}>Different amount depending on the customer's answers</Button>
      </div>
      {!inst.matrix ? (
        <div className="space-y-1"><Label className="text-[10px]">Amount (₹, GST-incl)</Label>
          <Input data-testid={`v2-inst-amount-${index}`} type="number" step="0.01" className="h-8" value={inst.amount ?? ""} onChange={(e) => set({ amount: e.target.value })} /></div>
      ) : (
        <div className="space-y-2">
          {(inst.matrix || []).map((row, i) => (
            <div key={i} className="border rounded-md p-2 space-y-1.5">
              <ConditionBuilder testPrefix={`v2-inst-${index}-row-${i}`} condition={row.if} onChange={(c) => setMatrixField(i, { if: c })} />
              <div className="flex items-center gap-2">
                <Label className="text-[10px]">then amount is</Label>
                <Input type="number" step="0.01" className="h-8 w-32" value={row.amount} onChange={(e) => setMatrixField(i, { amount: e.target.value })} />
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2 ml-auto" onClick={() => removeRow(i)}>Remove row</Button>
              </div>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={addRow}><Plus className="h-3.5 w-3.5 mr-1" />Add row</Button>
        </div>
      )}
    </div>
  );
};

const FeeItemRow = ({ item, index, instalmentKeys, defaultGstRate, onChange, onRemove }) => {
  const [showCondition, setShowCondition] = useState(!!(item.condition && Object.keys(item.condition).length));
  const set = (patch) => onChange(index, { ...item, ...patch });
  const schedule = item.schedule || [];
  const setSchedule = (s) => set({ schedule: s });
  const isReimbursement = Number(item.reimbursement_amount || 0) > 0;
  const toggleReimbursement = (checked) => {
    if (checked) set({ reimbursement_amount: item.amount || "0", gst_rate: 0 });
    else set({ reimbursement_amount: "0", gst_rate: defaultGstRate ?? 0.18 });
  };
  const setAmount = (v) => set({ amount: v, ...(isReimbursement ? { reimbursement_amount: v } : {}) });
  const isSplit = schedule.length > 1;
  const setMode = (split) => {
    if (split) {
      if (schedule.length === 0) setSchedule([{ instalment_key: "", portion: 0.5 }, { instalment_key: "", portion: 0.5 }]);
      else if (schedule.length === 1) setSchedule([{ ...schedule[0], portion: 0.5 }, { instalment_key: "", portion: 0.5 }]);
    } else {
      setSchedule([{ instalment_key: schedule[0]?.instalment_key || "", portion: 1 }]);
    }
  };
  const addPart = () => setSchedule([...schedule, { instalment_key: "", portion: 0 }]);
  const removePart = (i) => setSchedule(schedule.filter((_, idx) => idx !== i));
  const updatePart = (i, patch) => setSchedule(schedule.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const portionSum = schedule.reduce((a, s) => a + (Number(s.portion) || 0), 0);
  return (
    <div className="border rounded-md p-2 space-y-2" data-testid={`v2-fee-item-${index}`}>
      <div className="flex items-center gap-2">
        <Input data-testid={`v2-fee-key-${index}`} className="h-8 w-32 font-mono text-xs" placeholder="key" value={item.key || ""} onChange={(e) => set({ key: e.target.value })} />
        <Input data-testid={`v2-fee-label-${index}`} className="h-8 flex-1" placeholder="Description" value={item.label || ""} onChange={(e) => set({ label: e.target.value })} />
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2" data-testid={`v2-fee-remove-${index}`} onClick={() => onRemove(index)}>×</Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1"><Label className="text-[10px]">Amount (₹)</Label>
          <Input data-testid={`v2-fee-amount-${index}`} type="number" step="0.01" className="h-8" value={item.amount ?? ""} onChange={(e) => setAmount(e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-[10px]">Applicable GST %</Label>
          <Input data-testid={`v2-fee-gst-${index}`} type="number" step="1" min="0" max="100" className="h-8" disabled={isReimbursement}
            value={item.gst_rate != null ? Math.round(item.gst_rate * 100) : ""} onChange={(e) => set({ gst_rate: Number(e.target.value) / 100 })} /></div>
        <div className="space-y-1"><Label className="text-[10px]">Multiply by (optional)</Label>
          <Input data-testid={`v2-fee-perunit-${index}`} className="h-8" placeholder="e.g. dependents" value={item.per_unit_of || ""} onChange={(e) => set({ per_unit_of: e.target.value || undefined })} /></div>
      </div>
      <p className="text-[10px] text-muted-foreground -mt-1">"Multiply by" — if this fee depends on a count (e.g. number of dependents), enter that field's name here. Leave blank for a flat fee.</p>
      <label className="flex items-start gap-1.5 text-xs">
        <input type="checkbox" className="mt-0.5" data-testid={`v2-fee-reimb-${index}`} checked={isReimbursement} onChange={(e) => toggleReimbursement(e.target.checked)} />
        <span>This is a reimbursement
          <span className="block text-[10px] text-muted-foreground font-normal">Money passed to someone else, not our fee — no GST, not counted as our revenue.</span>
        </span>
      </label>
      {!showCondition ? (
        <Button type="button" variant="link" size="sm" className="h-6 px-0 text-xs" onClick={() => setShowCondition(true)}>+ Only applies in certain cases</Button>
      ) : (
        <div className="space-y-1 border rounded-md p-2">
          <div className="flex items-center justify-between">
            <Label className="text-[10px]">Only charge this fee when…</Label>
            <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => { setShowCondition(false); set({ condition: undefined }); }}>Remove</Button>
          </div>
          <ConditionBuilder testPrefix={`v2-fee-${index}`} condition={item.condition} onChange={(c) => set({ condition: Object.keys(c).length ? c : undefined })} />
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-[10px]">Collected</Label>
        <div className="flex gap-4 text-xs">
          <label className="flex items-center gap-1.5"><input type="radio" checked={!isSplit} onChange={() => setMode(false)} />In full, with one instalment</label>
          <label className="flex items-center gap-1.5"><input type="radio" checked={isSplit} onChange={() => setMode(true)} />Split across multiple instalments</label>
        </div>
        {!isSplit ? (
          <select data-testid={`v2-fee-schedule-${index}`} className={selectCls + " w-full"} value={schedule[0]?.instalment_key || ""}
            onChange={(e) => setSchedule([{ instalment_key: e.target.value, portion: 1 }])}>
            <option value="">Which instalment?</option>
            {instalmentKeys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        ) : (
          <div className="space-y-1.5">
            {schedule.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground w-12">Part {i + 1}</span>
                <select className={selectCls + " flex-1"} value={s.instalment_key} data-testid={`v2-fee-part-inst-${index}-${i}`}
                  onChange={(e) => updatePart(i, { instalment_key: e.target.value })}>
                  <option value="">select instalment…</option>
                  {instalmentKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <Input type="number" min="0" max="100" className="h-8 w-20 text-xs" data-testid={`v2-fee-part-pct-${index}-${i}`}
                  value={Math.round((Number(s.portion) || 0) * 100)} onChange={(e) => updatePart(i, { portion: Number(e.target.value) / 100 })} />
                <span className="text-xs text-muted-foreground">%</span>
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => removePart(i)}>×</Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addPart}><Plus className="h-3.5 w-3.5 mr-1" />Add part</Button>
            <p className={`text-[11px] ${Math.abs(portionSum - 1) > 0.001 ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}>
              Total: {Math.round(portionSum * 100)}%{Math.abs(portionSum - 1) > 0.001 ? " — parts must add up to 100%" : ""}</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* Structured Commercial Plan version editor — real configuration control.
   Common pricing fields are directly editable; the full JSON remains
   available for advanced formula/rule changes.

   2026-09 Commercial Integrity (Phase E0): this editor no longer publishes
   directly — "Save Draft" writes to the new Draft/Publish lifecycle
   (POST /finance/commercial-plans/{product_code}/draft), which never
   touches the live Published plan (production always reads
   get_active_plan(), which only ever sees active:True). Publishing is a
   separate, explicit step in DraftReviewDialog below, after reviewing the
   exact diff against the current Published version. */
const PlanEditor = ({ base, open, setOpen, onDraftSaved }) => {
  const [draft, setDraft] = useState(null);   // parsed object
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [siIfDraft, setSiIfDraft] = useState({}); // row index -> raw condition text being typed
  const [tiIfDraft, setTiIfDraft] = useState({});
  const [stageTriggerOptions, setStageTriggerOptions] = useState([]);
  useEffect(() => {
    if (base) {
      // Resuming an already-saved Draft keeps its own name verbatim (it's
      // not being forked again); forking fresh off a Published version (or
      // the synthetic v0 shell for a brand-new product) suggests the next
      // version number in the name, same as before.
      const name = base.status === "draft" ? base.name : base.name.replace(/v\d+\s*$/, `v${base.version + 1}`);
      const d = { name, gst_rate: base.gst_rate, currency: base.currency, components: JSON.parse(JSON.stringify(base.components || {})), reminders: JSON.parse(JSON.stringify(base.reminders || {})) };
      setDraft(d); setJsonText(JSON.stringify(d, null, 2)); setJsonMode(false);
      setSiIfDraft({}); setTiIfDraft({});
    }
  }, [base]);
  // Stage-aware trigger picker (P0 #13/#14): a milestone `trigger` can now
  // be set to a REAL configured stage name — this product's own sales-
  // pipeline stages AND its Process/Case workflow stages — instead of only
  // a hand-typed business-milestone string, so an obligation activates
  // automatically the moment that stage is actually reached (see backend
  // finance.activate_stage_triggered_obligations). Generic per-product —
  // nothing here is hardcoded to a specific pipeline or workflow.
  useEffect(() => {
    if (!base?.product_code) { setStageTriggerOptions([]); return; }
    Promise.all([
      api.get("/opportunities/pipeline-stages", { params: { product_code: base.product_code } }).catch(() => ({ data: [] })),
      api.get("/cases/workflows").catch(() => ({ data: {} })),
    ]).then(([stagesRes, workflowsRes]) => {
      const salesStages = (stagesRes.data || []).map((s) => s.name);
      const processStages = workflowsRes.data?.[base.product_code] || [];
      setStageTriggerOptions([...new Set([...salesStages, ...processStages])]);
    });
  }, [base?.product_code]);
  if (!base || !draft) return null;
  const c = draft.components || {};
  const si = c.second_instalment || {};
  const ti = c.third_instalment || {};
  const setComp = (k, v) => setDraft({ ...draft, components: { ...c, [k]: v } });
  const setSi = (k, v) => setComp("second_instalment", { ...si, [k]: v });
  const setTi = (k, v) => setComp("third_instalment", { ...ti, [k]: v });
  const setMatrixAmt = (i, v) => {
    const m = [...(si.matrix || [])]; m[i] = { ...m[i], amount: v }; setSi("matrix", m);
  };
  const setThirdMatrixAmt = (i, v) => {
    const m = [...(ti.matrix || [])]; m[i] = { ...m[i], amount: v }; setTi("matrix", m);
  };
  const setMatrixIf = (i, obj) => {
    const m = [...(si.matrix || [])]; m[i] = { ...m[i], if: obj }; setSi("matrix", m);
  };
  const setThirdMatrixIf = (i, obj) => {
    const m = [...(ti.matrix || [])]; m[i] = { ...m[i], if: obj }; setTi("matrix", m);
  };
  const addMatrixRow = () => setSi("matrix", [...(si.matrix || []), { if: {}, amount: "0.00" }]);
  const removeMatrixRow = (i) => { setSi("matrix", (si.matrix || []).filter((_, idx) => idx !== i)); const d = { ...siIfDraft }; delete d[i]; setSiIfDraft(d); };
  const addThirdMatrixRow = () => setTi("matrix", [...(ti.matrix || []), { if: {}, amount: "0.00" }]);
  const removeThirdMatrixRow = (i) => { setTi("matrix", (ti.matrix || []).filter((_, idx) => idx !== i)); const d = { ...tiIfDraft }; delete d[i]; setTiIfDraft(d); };

  // ---- schema v2 (instalments + fee_items) ----
  const isV2 = c.schema_version === 2;
  const v2Instalments = c.instalments || [];
  const v2FeeItems = c.fee_items || [];
  const v2Keys = v2Instalments.map((i) => i.key).filter(Boolean);
  const setInstalments = (list) => setComp("instalments", list);
  const setFeeItems = (list) => setComp("fee_items", list);
  const updateInstalment = (i, val) => setInstalments(v2Instalments.map((x, idx) => idx === i ? val : x));
  const removeInstalment = (i) => setInstalments(v2Instalments.filter((_, idx) => idx !== i));
  const addInstalment = () => setInstalments([...v2Instalments, { key: `instalment_${v2Instalments.length + 1}`, label: "New Instalment", amount: "0.00", gst_rate: draft.gst_rate ?? 0.18, activation: "immediate" }]);
  const toggleInstalmentMatrix = (i, useMatrix) => {
    const inst = v2Instalments[i];
    const next = { ...inst };
    if (useMatrix) { delete next.amount; next.matrix = next.matrix || [{ if: {}, amount: "0.00" }]; }
    else { delete next.matrix; next.amount = next.amount || "0.00"; }
    updateInstalment(i, next);
  };
  const updateFeeItem = (i, val) => setFeeItems(v2FeeItems.map((x, idx) => idx === i ? val : x));
  const removeFeeItem = (i) => setFeeItems(v2FeeItems.filter((_, idx) => idx !== i));
  const addFeeItem = () => setFeeItems([...v2FeeItems, { key: `fee_${v2FeeItems.length + 1}`, label: "New fee item", amount: "0.00", gst_rate: draft.gst_rate ?? 0.18, reimbursement_amount: "0", schedule: [] }]);
  const convertToV2 = () => setDraft({ ...draft, components: convertLegacyToV2(c) });

  const rem = draft.reminders || {};
  const setRem = (grp, k, v) => setDraft({ ...draft, reminders: { ...rem, [grp]: { ...(rem[grp] || {}), [k]: Number(v) } } });
  const removeRemGroup = (grp) => { const { [grp]: _drop, ...restRem } = rem; setDraft({ ...draft, reminders: restRem }); };
  const addRemGroup = (grp) => setDraft({ ...draft, reminders: { ...rem, [grp]: { first_day: 0, second_day: 7, escalation_days_before: 7, deadline_days: 21, npa_days_after: 14 } } });
  // Real obligation keys, not bare item keys — a fee item's actual
  // obligation is keyed `fee:{item_key}:{instalment_key}` (one per
  // schedule entry — see compute_obligations_v2 in finance.py), and that
  // compound string is exactly what reminders.get(obligation_key) looks
  // up at activation time. Keying this UI by the bare item key would
  // silently produce reminder config that's never found at runtime.
  const v2FeeReminderEntries = v2FeeItems.flatMap((f) =>
    (f.schedule || []).filter((s) => s.instalment_key).map((s) => ({
      key: `fee:${f.key}:${s.instalment_key}`,
      display: `${f.label || f.key} — with ${s.instalment_key}`,
    })));
  const v2ReminderKeys = [
    ...v2Keys.map((k) => ({ key: k, display: k })),
    ...v2FeeReminderEntries,
  ].filter((e) => e.key);
  const saveDraft = async () => {
    try {
      const body = jsonMode ? JSON.parse(jsonText) : draft;
      const { data: savedDraft } = await api.post(`/finance/commercial-plans/${base.product_code}/draft`, {
        name: body.name || `${base.product_code} — Draft`,
        currency: body.currency || "INR", gst_rate: body.gst_rate ?? 0.18,
        components: body.components, reminders: body.reminders || {},
      });
      toast.success("Draft saved — review the diff before publishing; customers are completely unaffected until then");
      setOpen(false); onDraftSaved(savedDraft);
    } catch (e) { toast.error(e.message?.includes("JSON") ? "Invalid JSON" : errDetail(e)); }
  };
  const NumField = ({ label, value, onChange, testId }) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Input data-testid={testId} type="number" step="0.01" className="h-8" value={value ?? ""} onChange={(e) => onChange(e.target.value)} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="cplan-editor">
        <DialogHeader><DialogTitle className="font-heading">
          {base.status === "draft" ? `Edit Draft — ${base.product_code}` : `New Draft — ${base.product_code} (v${base.version + 1})`}
        </DialogTitle></DialogHeader>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Advanced JSON mode</Label>
          <Switch data-testid="cplan-json-toggle" checked={jsonMode} onCheckedChange={(v) => { if (v) setJsonText(JSON.stringify(draft, null, 2)); else { try { setDraft(JSON.parse(jsonText)); } catch { toast.error("Invalid JSON — staying in JSON mode"); return; } } setJsonMode(v); }} />
        </div>
        {jsonMode ? (
          <Textarea data-testid="cplan-json" className="font-mono text-xs h-96" value={jsonText} onChange={(e) => setJsonText(e.target.value)} />
        ) : (
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Version name</Label>
              <Input data-testid="cplan-name" className="h-8" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
            <div className="space-y-1">
              <Label className="text-xs">Pricing model</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={c.mode === "simple_fee" ? "default" : "outline"}
                  data-testid="cplan-mode-simple" onClick={() => setComp("mode", "simple_fee")}>Simple / variable fee</Button>
                <Button type="button" size="sm" variant={c.mode !== "simple_fee" ? "default" : "outline"}
                  data-testid="cplan-mode-structured" onClick={() => { const { mode, ...rest } = c; setDraft({ ...draft, components: rest }); }}>Structured / instalment-based</Button>
              </div>
            </div>
            <datalist id="finance-known-triggers">
              {KNOWN_TRIGGERS.map((t) => <option key={t} value={t} />)}
              {stageTriggerOptions.map((t) => <option key={t} value={t} />)}
            </datalist>
            {c.mode === "simple_fee" ? (
              <div className="space-y-1"><Label className="text-xs">Fee label</Label>
                <Input data-testid="cplan-fee-label" className="h-8" value={c.fee_label || ""} onChange={(e) => setComp("fee_label", e.target.value)} />
                <p className="text-[11px] text-muted-foreground">Simple / variable-fee product — the consultant enters the GST-inclusive professional fee per customer; SalesOS derives taxable value and GST.</p></div>
            ) : isV2 ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Instalments (any number, any order)</Label>
                  {v2Instalments.map((inst, i) => (
                    <InstalmentRow key={i} inst={inst} index={i} allKeys={v2Keys}
                      onChange={updateInstalment} onRemove={removeInstalment} onToggleMatrix={toggleInstalmentMatrix} />
                  ))}
                  <Button type="button" size="sm" variant="outline" data-testid="v2-add-instalment" onClick={addInstalment}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add instalment</Button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fee items — add-ons, dependent/spouse fees, anything billed alongside an instalment (in full or split)</Label>
                  {v2FeeItems.map((item, i) => (
                    <FeeItemRow key={i} item={item} index={i} instalmentKeys={v2Keys}
                      defaultGstRate={draft.gst_rate ?? 0.18}
                      onChange={updateFeeItem} onRemove={removeFeeItem} />
                  ))}
                  <Button type="button" size="sm" variant="outline" data-testid="v2-add-fee-item" onClick={addFeeItem}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add fee item</Button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Reminder / NPA day-offsets (one block per instalment, or per fee item + instalment it's collected with)</Label>
                  {v2ReminderKeys.map(({ key, display }) => (
                    <div key={key} className="border rounded-md p-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs">{display}</span>
                        {rem[key] ? (
                          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => removeRemGroup(key)}>Remove reminders</Button>
                        ) : (
                          <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => addRemGroup(key)}>Add reminders</Button>
                        )}
                      </div>
                      {rem[key] && (
                        <div className="grid grid-cols-3 gap-2">
                          {Object.entries(rem[key]).map(([k, v]) => (
                            <div key={k} className="space-y-0.5"><Label className="text-[10px]">{k.replaceAll("_", " ")}</Label>
                              <Input type="number" className="h-7 text-xs" value={v} onChange={(e) => setRem(key, k, e.target.value)} /></div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <Button type="button" size="sm" variant="outline" data-testid="cplan-convert-v2" onClick={convertToV2}>
                  Convert to new flexible pricing model →</Button>
                <p className="text-[11px] text-muted-foreground -mt-1">Translates the fields below into the new instalment/fee-item builder with the same numbers — review before publishing.</p>
                <div className="grid grid-cols-2 gap-2">
                  <NumField testId="cplan-registration" label="Registration fee (₹, GST-incl)" value={c.registration_fee} onChange={(v) => setComp("registration_fee", v)} />
                  <NumField testId="cplan-booking" label="Booking amount (₹, GST-incl)" value={c.booking_amount} onChange={(v) => setComp("booking_amount", v)} />
                  <NumField testId="cplan-dep-alloc" label="Dependent 2nd-instalment addition (₹/child, taxable)" value={si.per_dependent} onChange={(v) => setSi("per_dependent", v)} />
                  <NumField testId="cplan-dep-alloc-reimb" label="Dependent 2nd-instalment addition (₹/child, reimbursement/no GST)" value={si.per_dependent_reimbursement} onChange={(v) => setSi("per_dependent_reimbursement", v)} />
                  <NumField testId="cplan-pte" label="PTE per learner (₹, taxable)" value={si.per_pte_learner} onChange={(v) => setSi("per_pte_learner", v)} />
                  <NumField testId="cplan-pte-reimb" label="PTE per learner (₹, reimbursement/no GST)" value={si.per_pte_learner_reimbursement} onChange={(v) => setSi("per_pte_learner_reimbursement", v)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Second-instalment base matrix (₹, GST-inclusive)</Label>
                  {(si.matrix || []).map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input data-testid={`cplan-matrix-if-${i}`} className="h-8 flex-1 font-mono text-[11px]"
                        value={siIfDraft[i] ?? JSON.stringify(row.if)}
                        onChange={(e) => setSiIfDraft({ ...siIfDraft, [i]: e.target.value })}
                        onBlur={(e) => { try { setMatrixIf(i, JSON.parse(e.target.value)); const d = { ...siIfDraft }; delete d[i]; setSiIfDraft(d); } catch { toast.error('Invalid JSON condition — e.g. {"primary_sa":true}'); } }} />
                      <Input data-testid={`cplan-matrix-${i}`} type="number" step="0.01" className="h-8 w-32" value={row.amount} onChange={(e) => setMatrixAmt(i, e.target.value)} />
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" data-testid={`cplan-matrix-remove-${i}`} onClick={() => removeMatrixRow(i)}>×</Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" data-testid="cplan-matrix-add" onClick={addMatrixRow}><Plus className="h-3.5 w-3.5 mr-1" />Add row</Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <NumField testId="cplan-third-dep" label="Dependent 3rd-instalment addition (₹/child, taxable)" value={ti.per_dependent} onChange={(v) => setTi("per_dependent", v)} />
                  <NumField testId="cplan-third-dep-reimb" label="Dependent 3rd-instalment addition (₹/child, reimbursement/no GST)" value={ti.per_dependent_reimbursement} onChange={(v) => setTi("per_dependent_reimbursement", v)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Third / final instalment — FIXED matrix (₹, GST-inclusive). Never a residual/leftover figure — a row with no matching condition means no third-instalment obligation is created at all.</Label>
                  {(ti.matrix || []).map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input data-testid={`cplan-third-matrix-if-${i}`} className="h-8 flex-1 font-mono text-[11px]"
                        value={tiIfDraft[i] ?? JSON.stringify(row.if)}
                        onChange={(e) => setTiIfDraft({ ...tiIfDraft, [i]: e.target.value })}
                        onBlur={(e) => { try { setThirdMatrixIf(i, JSON.parse(e.target.value)); const d = { ...tiIfDraft }; delete d[i]; setTiIfDraft(d); } catch { toast.error('Invalid JSON condition — e.g. {"primary_sa":true}'); } }} />
                      <Input data-testid={`cplan-third-matrix-${i}`} type="number" step="0.01" className="h-8 w-32" value={row.amount} onChange={(e) => setThirdMatrixAmt(i, e.target.value)} />
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" data-testid={`cplan-third-matrix-remove-${i}`} onClick={() => removeThirdMatrixRow(i)}>×</Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" data-testid="cplan-third-matrix-add" onClick={addThirdMatrixRow}><Plus className="h-3.5 w-3.5 mr-1" />Add row</Button>
                </div>
                <div className="space-y-1"><Label className="text-xs">Third-instalment trigger</Label>
                  <Input data-testid="cplan-trigger" className="h-8" list="finance-known-triggers" value={ti.trigger || ""} onChange={(e) => setTi("trigger", e.target.value)} />
                  <p className="text-[11px] text-muted-foreground">A real stage name from this product's Sales pipeline or Process workflow (start typing for suggestions) activates automatically when that stage is reached — or a business milestone like positive_skill_assessment / successful_admission that isn't tied to a stage.</p></div>
                {rem.second && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Second-instalment reminder / NPA day-offsets</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(rem.second).map(([k, v]) => (
                        <div key={k} className="space-y-0.5"><Label className="text-[10px]">{k.replaceAll("_", " ")}</Label>
                          <Input data-testid={`cplan-rem-${k}`} type="number" className="h-7 text-xs" value={v} onChange={(e) => setRem("second", k, e.target.value)} /></div>
                      ))}
                    </div>
                  </div>
                )}
                {rem.third && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Third-instalment reminder / NPA day-offsets</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(rem.third).map(([k, v]) => (
                        <div key={k} className="space-y-0.5"><Label className="text-[10px]">{k.replaceAll("_", " ")}</Label>
                          <Input data-testid={`cplan-rem3-${k}`} type="number" className="h-7 text-xs" value={v} onChange={(e) => setRem("third", k, e.target.value)} /></div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <DialogFooter><Button data-testid="cplan-save-draft" onClick={saveDraft}>Save Draft</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Field-level diff between a Draft and the Published version it was forked
   from (or "no Published version" for a brand-new product's first plan).
   Deliberately shallow (per top-level component/reminders key, not a full
   recursive tree diff) — enough for an admin to see exactly what changed
   and cite the agreement clause justifying it, without over-engineering a
   generic JSON-diff algorithm for what is, in practice, a small number of
   top-level sections. */
const FIELD_LABELS = { name: "Version name", currency: "Currency", gst_rate: "GST rate",
  components: "Pricing components", reminders: "Reminders / NPA schedule", notes: "Notes" };
const diffFields = (draft, base) => {
  const keys = ["name", "currency", "gst_rate", "components", "reminders", "notes"];
  const norm = (v) => (v === undefined || v === null ? null : v); // null/undefined/"" all read as "nothing" here
  return keys.map((k) => ({
    key: k, label: FIELD_LABELS[k],
    before: base ? base[k] : undefined,
    after: draft[k],
    changed: JSON.stringify(norm(base ? base[k] : undefined)) !== JSON.stringify(norm(draft[k])),
  })).filter((f) => f.changed || f.key === "components"); // pricing always shown, even unchanged
};
const DiffValue = ({ v }) => {
  if (v === undefined || v === null) return <span className="text-muted-foreground italic">— none —</span>;
  if (typeof v === "object") return <pre className="text-[10px] font-mono whitespace-pre-wrap bg-muted/40 rounded p-1.5 max-h-48 overflow-auto">{JSON.stringify(v, null, 2)}</pre>;
  return <span className="font-mono text-xs">{String(v)}</span>;
};

const DraftReviewDialog = ({ draft, base, open, setOpen, onEditAgain, onDone }) => {
  const [busy, setBusy] = useState(false);
  if (!draft) return null;
  const fields = diffFields(draft, base);
  const publishDraft = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/commercial-plans/${draft.plan_id}/publish`);
      toast.success(`Published — ${draft.product_code} v${draft.version} is now live for new customers`);
      setOpen(false); onDone();
    } catch (e) {
      const detail = errDetail(e);
      toast.error(detail.includes("re-fork") || detail.includes("stale")
        ? `Stale — ${detail}` : detail, { duration: 8000 });
    } finally { setBusy(false); }
  };
  const discardDraft = async () => {
    setBusy(true);
    try {
      await api.delete(`/finance/commercial-plans/${draft.plan_id}/draft`);
      toast.success("Draft discarded");
      setOpen(false); onDone();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="cplan-draft-review">
        <DialogHeader><DialogTitle className="font-heading">
          Review Draft — {draft.product_code}: {base ? `v${base.version}` : "no Published version"} → v{draft.version}
        </DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Nothing below is live yet — customers, calculations and Create &amp; Calculate all still use the
          current Published version{base ? ` (v${base.version})` : " (none)"} until you explicitly Publish.
        </p>
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.key} className="border rounded-md p-2 space-y-1.5" data-testid={`cplan-diff-${f.key}`}>
              <div className="flex items-center gap-2">
                <Label className="text-xs font-medium">{f.label}</Label>
                {f.changed && <Badge variant="outline" className="text-[10px]">changed</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><div className="text-[10px] text-muted-foreground mb-0.5">Current Published</div><DiffValue v={f.before} /></div>
                <div><div className="text-[10px] text-muted-foreground mb-0.5">Proposed Draft</div><DiffValue v={f.after} /></div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="cplan-draft-edit-again" disabled={busy} onClick={() => onEditAgain(draft)}>Keep editing</Button>
          <Button variant="outline" size="sm" className="text-destructive" data-testid="cplan-draft-discard" disabled={busy} onClick={discardDraft}>Discard Draft</Button>
          <Button size="sm" className="ml-auto" data-testid="cplan-draft-publish" disabled={busy} onClick={publishDraft}>Publish</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Company / document template configuration (Administration-controlled;
   generated documents keep their generation-time snapshot). */
const TemplateSettings = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/finance/template-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const F = ({ k, label, area }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {area ? <Textarea data-testid={`tpl-${k}`} rows={2} className="text-xs" value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} />
        : <Input data-testid={`tpl-${k}`} className="h-8" value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} />}
    </div>
  );
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/finance/template-settings", { settings: s });
      toast.success("Template settings saved — historical documents keep their snapshot");
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="finance-template-settings">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" />Finance / Document template settings</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <F k="legal_name" label="Company legal / trading name" />
          <F k="gstin" label="GSTIN" />
          <F k="phone" label="Phone" />
          <F k="email" label="Email" />
          <F k="website" label="Website" />
          <F k="signature_line" label="Authorised signatory line" />
          <F k="receipt_prefix" label="Receipt number prefix" />
          <F k="invoice_prefix" label="Invoice number prefix" />
        </div>
        <F k="registered_address" label="Registered address" area />
        <F k="bank_instructions" label="Bank / payment instructions" area />
        <F k="terms" label="Terms & Conditions" area />
        <div className="grid grid-cols-2 gap-2">
          <F k="receipt_footer" label="Receipt footer" area />
          <F k="invoice_footer" label="Invoice footer" area />
        </div>
        <Button data-testid="tpl-save" size="sm" disabled={busy} onClick={save}><Save className="h-3.5 w-3.5 mr-1.5" />Save template settings</Button>
      </CardContent>
    </Card>
  );
};

/* Product catalog — real CRUD. A product created here shows up immediately
   in every product picker across the app (they all read GET /products
   live) and in the Commercial Plans list below. */
const ProductsCard = ({ products, reload }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeEdited, setCodeEdited] = useState(false);
  const [parentCode, setParentCode] = useState("");
  const [saving, setSaving] = useState(false);

  const suggestCode = (n) => n.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const onNameChange = (v) => {
    setName(v);
    if (!codeEdited) setCode(suggestCode(v));
  };
  const parentEligible = products.filter((p) => !p.parent_code);

  const create = async () => {
    if (!name.trim() || !code.trim()) { toast.error("Name and code required"); return; }
    setSaving(true);
    try {
      await api.post("/products", { name: name.trim(), code: code.trim(), parent_code: parentCode || null });
      toast.success(`Product ${code.trim().toUpperCase()} created`);
      setOpen(false); setName(""); setCode(""); setCodeEdited(false); setParentCode("");
      reload();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try { await api.patch(`/products/${p.code}`, { active: !p.active }); reload(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const toggleOutcomeBlocked = async (p) => {
    try {
      await api.patch(`/products/${p.code}`, { consultation_outcome_blocked: !p.consultation_outcome_blocked });
      reload();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Card data-testid="products-admin">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm font-heading flex items-center gap-2"><Package className="h-4 w-4 text-primary" />Products</CardTitle>
          <p className="text-xs text-muted-foreground">The product catalog used everywhere in SalesOS. A new product appears immediately in every product picker; give it a Commercial Plan below to start pricing it.</p>
        </div>
        <Button data-testid="add-product-btn" size="sm" variant="outline" onClick={() => setOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add product
        </Button>
      </CardHeader>
      <CardContent className="space-y-1">
        {products.map((p) => (
          <div key={p.code} className="p-2 rounded border text-xs flex items-center gap-2 flex-wrap" data-testid={`product-row-${p.code}`}>
            <b>{p.name}</b>
            <code className="font-mono bg-muted px-1 rounded">{p.code}</code>
            {p.parent_code && <Badge variant="outline" className="text-[10px]">child of {p.parent_code}</Badge>}
            <label className="flex items-center gap-1.5 ml-auto cursor-pointer">
              <span className="text-muted-foreground">{p.consultation_outcome_blocked ? "blocked from outcome" : "allowed at outcome"}</span>
              <Switch data-testid={`product-outcome-block-${p.code}`} checked={!!p.consultation_outcome_blocked} onCheckedChange={() => toggleOutcomeBlocked(p)} />
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <span className="text-muted-foreground">{p.active ? "active" : "inactive"}</span>
              <Switch data-testid={`product-active-${p.code}`} checked={p.active} onCheckedChange={() => toggleActive(p)} />
            </label>
          </div>
        ))}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Name</Label>
              <Input data-testid="new-product-name" value={name} onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Skilled Employer Sponsored Visa" /></div>
            <div className="space-y-1"><Label className="text-xs">Code</Label>
              <Input data-testid="new-product-code" className="font-mono" value={code} onChange={(e) => { setCode(e.target.value); setCodeEdited(true); }} placeholder="SESV" />
              <p className="text-[11px] text-muted-foreground">Uppercase letters/numbers/underscores. Can't be changed after creation.</p></div>
            <div className="space-y-1"><Label className="text-xs">Parent / family product (optional)</Label>
              <select data-testid="new-product-parent" className={selectCls + " w-full"} value={parentCode} onChange={(e) => setParentCode(e.target.value)}>
                <option value="">None (standalone / itself a family)</option>
                {parentEligible.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select>
              <p className="text-[11px] text-muted-foreground">If set, this product can only be chosen at consultation signup — leads/bookings stay at the parent level until then.</p></div>
          </div>
          <DialogFooter><Button data-testid="create-product-submit" onClick={create} disabled={saving}>Create product</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const FinanceAdminTab = () => {
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [editOpen, setEditOpen] = useState(null); // base plan for new version
  const [reviewDraft, setReviewDraft] = useState(null); // { draft, base } for DraftReviewDialog
  const [modeChoiceProduct, setModeChoiceProduct] = useState(null); // product awaiting simple/structured choice
  const load = useCallback(() => api.get("/finance/commercial-plans").then((r) => setPlans(r.data)).catch(() => {}), []);
  const loadProducts = useCallback(() => api.get("/products").then((r) => setProducts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadProducts(); }, [load, loadProducts]);

  const grouped = plans.reduce((acc, p) => { (acc[p.product_code] = acc[p.product_code] || []).push(p); return acc; }, {});

  const createFirstPlan = (product, mode) => {
    const base = { product_code: product.code, version: 0, name: `${product.name} v0`,
                   gst_rate: 0.18, currency: "INR" };
    setEditOpen(mode === "structured" ? {
      ...base,
      components: { schema_version: 2, instalments: [
        { key: "registration", label: "Registration Fee", amount: "0.00", gst_rate: 0.18, activation: "immediate", gates_booking_date: true },
        { key: "booking", label: "Booking Amount", amount: "0.00", gst_rate: 0.18, activation: "immediate", gates_booking_date: true },
      ], fee_items: [] },
      reminders: {},
    } : {
      ...base, components: { mode: "simple_fee", fee_label: product.name }, reminders: {},
    });
    setModeChoiceProduct(null);
  };

  return (
    <div className="space-y-4 pt-2" data-testid="admin-finance-tab">
      <p className="text-sm text-muted-foreground">Versioned pricing engine. Publishing a new version affects NEW customers only — historical payment plans, receipts and invoices are never recalculated. All amounts are GST-inclusive.</p>
      <ProductsCard products={products} reload={loadProducts} />
      <TemplateSettings />
      {products.map((product) => {
        const rows = grouped[product.code] || [];
        const activeRow = rows.find((p) => p.active);
        const draftRow = rows.find((p) => p.status === "draft");
        const historyRows = rows.filter((p) => p !== activeRow && p !== draftRow);
        const renderPlanCard = (p) => {
          const c = p.components || {}; const si = c.second_instalment || {};
          return (
            <div key={p.plan_id} className="rounded-md border p-3 text-sm space-y-1" data-testid={`cplan-${p.product_code}-v${p.version}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{p.name}</span>
                <Badge variant={p.active ? "default" : "outline"}>{p.active ? "active" : `v${p.version} (historical)`}</Badge>
                <span className="text-xs text-muted-foreground">GST {(p.gst_rate * 100).toFixed(0)}% incl.</span>
                {/* Editing now always goes through the Draft/Publish flow —
                    a NEW version is never published directly from here.
                    Hidden while a Draft is already pending for this
                    product (see the banner below), so there's exactly one
                    place to resume editing, never two that could silently
                    clobber each other's unsaved state. */}
                {p.active && !draftRow && <Button size="sm" variant="outline" className="ml-auto" data-testid={`cplan-new-version-${product.code}`} onClick={() => setEditOpen(p)}><PlusCircle className="h-4 w-4 mr-1" />Edit (creates a Draft)</Button>}
              </div>
              {c.mode === "simple_fee" ? (
                <p className="text-xs text-muted-foreground">Simple / variable fee — consultant enters the GST-inclusive professional fee per customer; SalesOS derives taxable value + GST. Label: <b>{c.fee_label}</b></p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>Registration: <b>{inr(c.registration_fee)}</b></div>
                    <div>Booking: <b>{inr(c.booking_amount)}</b></div>
                    <div>Dependent 2nd-inst allocation: <b>{inr(si.per_dependent)}</b></div>
                    <div>Per PTE learner: <b>{inr(si.per_pte_learner)}</b></div>
                    {c.dependent_commercial_price && <div>Dependent commercial price: <b>{inr(c.dependent_commercial_price)}</b> /child (full package)</div>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    2nd instalment matrix: {(si.matrix || []).map((r, i) => <span key={i} className="mr-2">{JSON.stringify(r.if)}→{inr(r.amount)}</span>)}
                  </div>
                  <div className="text-xs text-muted-foreground">3rd instalment: residual, triggered by <b>{c.third_instalment?.trigger?.replaceAll("_", " ")}</b>{p.reminders?.second && <> · Reminders: D{p.reminders.second.first_followup_day}/D{p.reminders.second.refund_window_end_day}/D{p.reminders.second.second_followup_day}/deadline D{p.reminders.second.deadline_day}/NPA D{p.reminders.second.npa_day}</>}</div>
                  {c.refund_note && <div className="text-xs text-[hsl(var(--warning))]">{c.refund_note}</div>}
                </>
              )}
            </div>
          );
        };
        return (
          <Card key={product.code}>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><IndianRupee className="h-4 w-4 text-primary" />{product.code}{!product.active && <Badge variant="outline">inactive product</Badge>}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {rows.length === 0 && (
                <div className="flex items-center justify-between text-sm" data-testid={`no-plan-${product.code}`}>
                  <span className="text-muted-foreground">No commercial plan yet.</span>
                  <Button size="sm" variant="outline" data-testid={`create-first-plan-${product.code}`} onClick={() => setModeChoiceProduct(product)}><PlusCircle className="h-4 w-4 mr-1" />Create first plan</Button>
                </div>
              )}
              {draftRow && (
                <div className="rounded-md border-2 border-amber-400 dark:border-amber-600 bg-amber-500/10 p-3 text-sm space-y-1"
                    data-testid={`cplan-draft-pending-${product.code}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{draftRow.name}</span>
                    <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400">Draft pending — v{draftRow.version}</Badge>
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`cplan-continue-draft-${product.code}`} onClick={() => setEditOpen(draftRow)}>Continue editing</Button>
                      <Button size="sm" data-testid={`cplan-review-draft-${product.code}`}
                        onClick={() => setReviewDraft({ draft: draftRow, base: activeRow || null })}>Review Diff</Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Not live — customers and Create &amp; Calculate still use {activeRow ? `Published v${activeRow.version}` : "no plan (none published yet)"} until this Draft is explicitly published.
                  </p>
                </div>
              )}
              {activeRow && renderPlanCard(activeRow)}
              {historyRows.map(renderPlanCard)}
            </CardContent>
          </Card>
        );
      })}
      <PlanEditor base={editOpen} open={!!editOpen} setOpen={(o) => !o && setEditOpen(null)}
        onDraftSaved={(savedDraft) => {
          load();
          const activeBase = (grouped[savedDraft.product_code] || []).find((p) => p.active) || null;
          setReviewDraft({ draft: savedDraft, base: activeBase });
        }} />
      <DraftReviewDialog draft={reviewDraft?.draft} base={reviewDraft?.base}
        open={!!reviewDraft} setOpen={(o) => !o && setReviewDraft(null)}
        onEditAgain={(draft) => { setReviewDraft(null); setEditOpen(draft); }}
        onDone={() => { setReviewDraft(null); load(); }} />
      <Dialog open={!!modeChoiceProduct} onOpenChange={(o) => !o && setModeChoiceProduct(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>First commercial plan — {modeChoiceProduct?.code}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-2">
            <Button variant="outline" className="justify-start items-start h-auto py-3 text-left whitespace-normal" data-testid="first-plan-simple"
              onClick={() => createFirstPlan(modeChoiceProduct, "simple")}>
              <div className="w-full"><div className="font-medium">Simple / variable fee</div>
                <div className="text-xs text-muted-foreground font-normal whitespace-normal">One GST-inclusive professional fee entered per customer — SalesOS derives taxable value + GST.</div></div>
            </Button>
            <Button variant="outline" className="justify-start items-start h-auto py-3 text-left whitespace-normal" data-testid="first-plan-structured"
              onClick={() => createFirstPlan(modeChoiceProduct, "structured")}>
              <div className="w-full"><div className="font-medium">Structured / instalment-based</div>
                <div className="text-xs text-muted-foreground font-normal whitespace-normal">Registration/booking fees, instalment matrices, add-ons, third-instalment trigger, reminders and overdue/NPA logic — same control as SM_INHOUSE's editor.</div></div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
